import { useDispatch, useSelector } from "react-redux";
import { ApiCallStatusV2, CANCELLED_REASONS } from "src/common/ApiCall";
import { RootState } from "src/redux/store";
import {
  CurrentAccessControlBasedOnSystemResource,
  CurrentAccessControlsBasedOnSystemResource,
} from "../model/currentAccessControlsBasedOnSystemResource";
import { useEffect, useState } from "react";
import { InputsforCurrentAccessControlsBasedOnSystemResource } from "../model/requestParams";
import axios, { CancelTokenSource } from "axios";
import { SystemResourceType } from "../model/systemResourceTypes";
import { AccessVisibilityActions } from "../model/model";
import { AuthZTable } from "src/components/authZTable";
import "../index.scss";
import { useHistory } from "react-router-dom";
import AuthZButton from "src/components/AuthzButton";

export function AccessVisibilityBasedOnSystemResource(props: {
  userEmail: Nullable<string>;
  filteredSystemResourceType: SystemResourceType;
  userGroup: Nullable<{
    id: string;
    type: string;
  }>;
  toShowFor: { user: boolean; userGroup: boolean };
  reduceTableHeight: string | null;
  isActive: boolean | null;
}) {
  const dispatch = useDispatch();
  const history = useHistory();
  const { search, pathname } = window.location;
  const urlSearchParams = new URLSearchParams(search);

  const accessVisibility = useSelector((state: RootState) => state.accessVisibility);

  const currentAccessVisibilityBasedOnSystemResourceApiCallStatus = new ApiCallStatusV2(
    accessVisibility.currentAccessControlsBasedOnSystemResources.progress,
    accessVisibility.currentAccessControlsBasedOnSystemResources.error
  );

  const currentAccessControlsBasedOnSystemResource = new CurrentAccessControlsBasedOnSystemResource(
    accessVisibility.currentAccessControlsBasedOnSystemResources.value.currentAccessControls,
    props.filteredSystemResourceType.isScopeApplicable(),
    {
      allCountries: accessVisibility.currentAccessControlsBasedOnSystemResources.value.allCountries,
      allRegions: accessVisibility.currentAccessControlsBasedOnSystemResources.value.allRegions,
    }
  );
  const totalCurrentAccessControlsBasedOnSystemResource =
    accessVisibility.currentAccessControlsBasedOnSystemResources.value.totalCount;

  const [pageSize, setPageSize] = useState<number>(10);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [filters, setFilters] = useState<{
    obtainedVia: string | null;
    resource: string | null;
    systemResource: string | null;
  }>({
    obtainedVia: null,
    resource: null,
    systemResource: null,
  });
  const [requestPayload, setRequestPayload] =
    useState<InputsforCurrentAccessControlsBasedOnSystemResource | null>(null);
  const [axiosCancelRequestsToken, setAxiosCancelRequestsToken] =
    useState<CancelTokenSource | null>(null);

  const dynamicColumns =
    props.filteredSystemResourceType.dynamicColumns()?.map((columnName) => ({
      title: columnName,
      dataIndex: columnName,
      key: columnName,
      render: (_text, record: CurrentAccessControlBasedOnSystemResource) => {
        return record.renderDynamicColumn(columnName);
      },
      onCell: (record: CurrentAccessControlBasedOnSystemResource, _rowIndex) => ({
        rowSpan: record.rowSpan.systemResourceSpan,
      }),
      sortEnabled: false,
      searchEnabled: false,
      width: "25%",
    })) ?? [];

  const scopeRelatedColumns = props.filteredSystemResourceType.isScopeApplicable()
    ? [
        {
          title: "Scope",
          dataIndex: "scope",
          key: "scope",
          render: (_text, record: CurrentAccessControlBasedOnSystemResource) => {
            return record.renderScope();
          },
          sortEnabled: false,
          searchEnabled: false,
          width: "15%",
        },
        {
          title: "Scope value",
          dataIndex: "scopeValue",
          key: "scopeValue",
          render: (_text, record: CurrentAccessControlBasedOnSystemResource) => {
            return record.renderScopeValue();
          },
          sortEnabled: false,
          searchEnabled: false,
          width: "15%",
        },
      ]
    : [];

  let columns = [
    {
      title: props.filteredSystemResourceType.name ?? "System Resources",
      dataIndex: "systemResource",
      key: "systemResource",
      render: (_text, record: CurrentAccessControlBasedOnSystemResource) => {
        return record.renderSystemResource();
      },
      onCell: (record: CurrentAccessControlBasedOnSystemResource, _rowIndex) => ({
        rowSpan: record.rowSpan.systemResourceSpan,
      }),
      sortEnabled: false,
      width: "15%",
    },
    ...dynamicColumns,
    ...scopeRelatedColumns,
    {
      title: "Obtained via",
      dataIndex: "obtainedVia",
      key: "obtainedVia",
      render: (_text, record: CurrentAccessControlBasedOnSystemResource) => {
        return record.obtainedVia.render(props.filteredSystemResourceType.id, "system-resources");
      },
      onCell: (record: CurrentAccessControlBasedOnSystemResource) => ({
        rowSpan: record.rowSpan.obtainedViaSpan,
      }),
      sortEnabled: false,
      width: "20%",
    },
    {
      title: "Information assets",
      dataIndex: "informationAssets",
      key: "informationAssets",
      render: (_text, record: CurrentAccessControlBasedOnSystemResource) => {
        return record.renderResource();
      },
      onCell: (record: CurrentAccessControlBasedOnSystemResource) => ({
        rowSpan: record.rowSpan.resourceSpan,
      }),
      sortEnabled: false,
      width: "15%",
    },
  ];

  function removeColumn(columnName) {
    columns = columns.filter((column) => column.title !== columnName);
  }

  if (props.toShowFor.userGroup === true || props.filteredSystemResourceType.isScopeApplicable()) {
    removeColumn("Obtained via");
  }

  function onCustomFilterAndCustomSortChange(filters: {
    systemResource: Array<string> | undefined;
    obtainedVia: Array<string> | undefined;
    informationAssets: Array<string> | undefined;
  }) {
    setFilters({
      obtainedVia: filters.obtainedVia?.[0] ?? null,
      resource: filters.informationAssets?.[0] ?? null,
      systemResource: filters.systemResource?.[0] ?? null,
    });
  }

  useEffect(() => {
    setRequestPayload(
      new InputsforCurrentAccessControlsBasedOnSystemResource({
        obtainedViaType: urlSearchParams.get("obtainedViaType") ?? null,
        resourceId: urlSearchParams.get("resourceId") ?? null,
        systemUserGroupId: urlSearchParams.get("systemUserGroupId") ?? null,
        systemId: props.filteredSystemResourceType.systemId,
        systemResourceType: props.filteredSystemResourceType.name,
        pageNumber: pageNumber,
        pageSize: pageSize,
        principalId: props.userEmail,
        userGroupId: urlSearchParams.get("userGroupId") ?? props.userGroup?.id ?? null,
        userGroupType: urlSearchParams.get("userGroupType") ?? props.userGroup?.type ?? null,
        systemResource: filters.systemResource,
        obtainedVia: filters.obtainedVia,
        resource: filters.resource,
        toShowFor: props.toShowFor,
      })
    );
  }, [
    JSON.stringify(props.filteredSystemResourceType.identifier()),
    pageNumber,
    pageSize,
    JSON.stringify(filters),
    search,
  ]);

  useEffect(() => {
    if (requestPayload !== null && props.isActive) {
      const axiosCancelToken = axios.CancelToken.source();
      dispatch(
        AccessVisibilityActions.getCurrentAccessControlsBasedOnSystemResource({
          requestBody: requestPayload.requestParams(),
          axiosConfig: {
            cancelToken: axiosCancelToken.token,
          },
        })
      );
      if (axiosCancelRequestsToken !== null) {
        axiosCancelRequestsToken.cancel(CANCELLED_REASONS.CalledApiAgain);
      }
      setAxiosCancelRequestsToken(axiosCancelToken);
    }
  }, [JSON.stringify(requestPayload?.requestParams())]);

  function renderDisclaimer() {
    // @ts-ignore
    if (urlSearchParams.size > 0) {
      return (
        <div className="disclaimer-content">
          <div className="disclaimer-msg">
            This page shows the details of the last selected resources. To view all resource
            details, please click Reset.
          </div>
          {/*@ts-ignore*/}
          <AuthZButton authZButtonType="default" onClick={() => history.push(pathname)}>
            Reset
          </AuthZButton>
        </div>
      );
    }
    return null;
  }

  return (
    <div className="current-access-controls-content">
      {currentAccessVisibilityBasedOnSystemResourceApiCallStatus.isFailed ? (
        <div className="error-text">Error loading details</div>
      ) : (
        <div>
          {renderDisclaimer()}
          <AuthZTable
            columns={columns}
            dataSource={currentAccessControlsBasedOnSystemResource.currentAccessControls}
            isFilteredOrSortedInBackend={true}
            loading={currentAccessVisibilityBasedOnSystemResourceApiCallStatus.isLoading}
            scroll={{ y: `calc(100vh - ${props.reduceTableHeight ?? "500px"})` }}
            pagination={{
              showSizeChanger: true,
              showQuickJumper: true,
              pageSize: pageSize,
              pageSizeOptions: [10, 25, 50],
              showTotal: (total: number, range: Array<number>) =>
                `${range[0]}-${range[1]} of ${total} items`,
              current: pageNumber,
              total: totalCurrentAccessControlsBasedOnSystemResource,
              onChange: (page: number, pageSize: number) => {
                setPageSize(pageSize);
                setPageNumber(page);
              },
            }}
            onCustomFilterAndCustomSortChange={onCustomFilterAndCustomSortChange}
            isCustomPaginationRequired={true}
          />
        </div>
      )}
    </div>
  );
}
